/**
 * @author Team CMS
 *
 * Utility functions for controlling video playback across different platforms.
 * Supports SolidTango and YouTube.
 */

import type { VideoType } from '../../react/VideoPlayer/VideoPlayer.types.js';
import { eventNotifier } from './eventNotifier.js';

/**
 * Defines the structure for sending messages to video players.
 */
type SendMessageProps = {
  id: string;
  event: 'message' | 'command';
  func?: string;
  args?: string | number | Array<string | number>;
  method?: string;
};

/**
 * Retrieves the video origin URL based on the video type.
 *
 * @param videoType - The type of video platform (e.g., 'solidtango', 'youtube').
 * @returns The origin URL for the specified video platform.
 */
export function getVideoOrigin(videoType?: VideoType): string {
  if (videoType === 'solidtango') {
    return process.env.NEXT_PUBLIC_SOLIDTANGO_ORIGIN ?? 'https://svspelsportcasino.solidtango.com';
  }
  return 'https://www.youtube-nocookie.com';
}

/**
 * Maps generic function names to YouTube-specific player commands.
 */
const youtubeCommandMap: Record<string, string> = {
  play: 'playVideo',
  pause: 'pauseVideo',
  stop: 'stopVideo',
  togglePlay: 'playVideo',
  seek: 'seekTo',
  volume: 'setVolume',
};

/**
 * Sends a message to a SolidTango iframe.
 *
 * @param videoFrame - The iframe element of the SolidTango video.
 * @param func - The function name to invoke on the video player.
 * @param value - Optional value to pass to the function.
 */
function sendMessageToSolidTango(videoFrame: HTMLIFrameElement, func: string, value?: string | number): void {
  const message: SendMessageProps = {
    id: videoFrame.id,
    event: 'message',
    func,
    args: value !== undefined ? value : undefined,
  };

  videoFrame?.contentWindow?.postMessage(JSON.stringify(message), getVideoOrigin('solidtango'));
}

/**
 * Sends a message to a YouTube iframe.
 *
 * @param videoFrame - The iframe element of the YouTube video.
 * @param func - The function name to invoke on the video player.
 * @param value - Optional value to pass to the function.
 */
export function sendMessageToYouTube(videoFrame: HTMLIFrameElement, func: string, value?: string | number): void {
  const mappedFunc = youtubeCommandMap[func];
  if (!mappedFunc) return;

  const message: SendMessageProps = {
    id: videoFrame.id,
    event: 'command',
    func: mappedFunc,
    args: func === 'volume' ? [Number(value) * 100] : value !== undefined ? [value] : undefined,
  };

  videoFrame?.contentWindow?.postMessage(JSON.stringify(message), getVideoOrigin('youtube'));
}

/**
 * Sends a message to the appropriate video player.
 *
 * @param videoId
 * @param func
 * @param value
 */
function sendMessage(videoId: string, func: string, value?: string | number): void {
  // eslint-disable-next-line unicorn/prefer-query-selector
  const el = document.getElementById(videoId) ?? document.getElementById(`video-${videoId}`);
  const videoFrame = el instanceof HTMLIFrameElement ? el : null;
  if (!videoFrame) return;

  const videoType = videoFrame.getAttribute('data-video-type') as VideoType | null;
  if (videoType === 'youtube') {
    sendMessageToYouTube(videoFrame, func, value);
  } else {
    sendMessageToSolidTango(videoFrame, func, value);
  }
}

/**
 * Retrieves the player state for a video player.
 *
 * @param videoId - The unique identifier for the video player.
 * @param command - The command to retrieve the state (e.g., 'ready', 'playing', 'position').
 * @param callback - The callback function to handle the retrieved state data.
 */
function getPlayerState(videoId: string, command: string, callback: (data: unknown) => void): void {
  const listener = function (data: unknown): void {
    // We got our callback, stop subscribing for more.
    eventNotifier?.removeEventListener(`${videoId}_${command}`, listener);
    callback(data);
  };
  eventNotifier?.addEventListener(`${videoId}_${command}`, listener);
  sendMessage(videoId, command);
}

/**
 * Video control functions
 */
export const VideoControls = {
  play: (videoId: string): void => sendMessage(videoId, 'play'),
  pause: (videoId: string): void => sendMessage(videoId, 'pause'),
  stop: (videoId: string): void => sendMessage(videoId, 'stop'),
  togglePlay: (videoId: string): void => sendMessage(videoId, 'togglePlay'),
  seek: (videoId: string, time: number): void => sendMessage(videoId, 'seek', time),
  setVolume: (videoId: string, volume: number): void => sendMessage(videoId, 'volume', volume),
  isReady: (videoId: string, callback: (data: unknown) => void): void => getPlayerState(videoId, 'ready', callback),
  isPlaying: (videoId: string, callback: (data: unknown) => void): void => getPlayerState(videoId, 'playing', callback),
  getPosition: (videoId: string, callback: (data: unknown) => void): void =>
    getPlayerState(videoId, 'position', callback),
  getDuration: (videoId: string, callback: (data: unknown) => void): void =>
    getPlayerState(videoId, 'duration', callback),
  getMetadata: (videoId: string, callback: (data: unknown) => void): void =>
    getPlayerState(videoId, 'metadata', callback),
};
